import type { CSSProperties, ReactNode, Ref } from "react";

/**
 * The frames' whole vocabulary: a hairline rule, a marker highlight, a pill,
 * and a "not yet" state. There is nothing else — no cards with shadows, no
 * badges, no icons beyond the two triangles and the microphone. (The desk's
 * document-kind icons, 2026-09-22, are the one later exception, and they
 * live with the desk in documents/kind-icon.tsx.)
 */

export function Rule({ width, className = "" }: { width?: string | undefined; className?: string | undefined }): ReactNode {
  return (
    <hr
      className={`border-0 border-t border-t-ink-soft ${className}`}
      style={width === undefined ? undefined : { width }}
    />
  );
}

/**
 * Frame 1 -> 2: hovering a landing nav item paints a highlighter behind it
 * AND opens the list up around it. Both halves are the hover state; the
 * marker alone reads as a plain background swatch.
 */
export function MarkerItem({
  children,
  onClick,
  disabled = false,
  active = false,
}: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  active?: boolean | undefined;
}): ReactNode {
  if (disabled) {
    return (
      <span aria-disabled="true" className="block cursor-default text-nav text-ink-faint select-none">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "block w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-display text-nav text-ink",
        "transition-[margin] duration-[var(--pc-dur)] ease-pc",
        "hover:my-[6px] hover:[&>span]:bg-[linear-gradient(var(--pc-marker),var(--pc-marker))]",
        active ? "my-[6px]" : "",
      ].join(" ")}
    >
      <span
        className={[
          "bg-no-repeat",
          "[background-size:calc(100%+4.2em)_0.62em] [background-position:0_0.18em]",
          active ? "bg-[linear-gradient(var(--pc-marker),var(--pc-marker))]" : "",
        ].join(" ")}
      >
        {children}
      </span>
    </button>
  );
}

/** The rounded outline button in frames 6/8/9/10/11 (`settings ▽`, `next ▷`). */
export function Pill({
  children,
  onClick,
  disabled = false,
  title,
  className = "",
  type = "button",
  ref,
}: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  title?: string | undefined;
  className?: string | undefined;
  /** "submit" for the one control that sends a form (the check-in, 2026-10-11), so Enter in the form sends it too. */
  type?: "button" | "submit" | undefined;
  /** The button itself, for a paper that hands focus back to it (the erasure dialog, 2026-10-20). */
  ref?: Ref<HTMLButtonElement> | undefined;
}): ReactNode {
  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-press="box"
      className={[
        "inline-flex items-center justify-center gap-4 rounded-pill border border-ink-soft bg-transparent",
        "px-6 py-2 font-sans text-nav",
        disabled ? "cursor-not-allowed text-ink-faint opacity-60" : "cursor-pointer text-ink hover:bg-marker",
        "transition-colors duration-[var(--pc-dur-fast)]",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/**
 * Everything on the cut list — Collaborate., events., the microphone, the
 * attach control — renders visibly, inertly, and says so. A hidden control
 * teaches the wrong thing about what the product is; a dead one that looks
 * live is worse.
 */
export function NotYet({ children, style }: { children: ReactNode; style?: CSSProperties | undefined }): ReactNode {
  return (
    <span
      aria-disabled="true"
      title="not yet."
      style={style}
      className="relative inline-flex cursor-not-allowed items-center gap-2 text-ink-faint select-none"
    >
      {children}
      <span className="text-micro tracking-wide">not yet.</span>
    </span>
  );
}

export function Eyebrow({ children }: { children: ReactNode }): ReactNode {
  return <div className="font-sans text-micro text-ink">{children}</div>;
}
